import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../LanguageContext';

const FooterContainer = styled.footer`
  background-color: ${({ theme }) => theme.colors.grayDark};
  color: ${({ theme }) => theme.colors.white};
  padding: 2rem 1rem 1rem;
  font-family: ${({ theme }) => theme.fonts.primary};
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 2rem;
`;

const Column = styled.div`
  flex: 1 1 250px;
  min-width: 200px;
`;

const ColumnTitle = styled.h4`
  margin-bottom: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.greenPrimary};
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const LinkItem = styled.li`
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.blueLight};
  }
`;

const Address = styled.address`
  font-style: normal;
  line-height: 1.5;
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 1.5rem;
`;

const SocialIcon = styled.a`
  color: ${({ theme }) => theme.colors.white};
  transition: color 0.3s ease;
  font-size: 24px;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.blueLight};
  }
`;

const LinkedInIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const YouTubeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
  </svg>
);

const Downloads = styled.div`
  margin-top: 1rem;
`;

const DownloadLink = styled.a`
  display: block;
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.blueLight};
  }
`;

const CreditsBar = styled.div`
  margin-top: 2rem;
  border-top: 1px solid ${({ theme }) => theme.colors.grayLight};
  padding-top: 1rem;
  font-size: 0.85rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.grayLight};
`;

const Footer = () => {
  const { translations } = useLanguage();

  return (
    <FooterContainer>
      <ContentWrapper>
        <Column>
          <ColumnTitle>{translations.footer.quickLinks}</ColumnTitle>
          <LinkList>
            {translations.footer.links.map((link, idx) => (
              <LinkItem key={idx}>{link}</LinkItem>
            ))}
          </LinkList>
        </Column>
        <Column>
          <ColumnTitle>{translations.footer.contactTitle}</ColumnTitle>
          <Address>
            {translations.footer.address.split('\n').map((line, idx) => (
              <React.Fragment key={idx}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </Address>
          <Downloads>
            {translations.footer.downloads.map((download, idx) => (
              <DownloadLink key={idx} href="#" tabIndex={0}>
                {download}
              </DownloadLink>
            ))}
          </Downloads>
        </Column>
        <Column>
          <ColumnTitle>{translations.footer.socialTitle}</ColumnTitle>
          <SocialIcons>
            <SocialIcon 
              href="https://www.linkedin.com/company/ecofab-equipamentos-ltda" 
              target="_blank" 
              rel="noopener noreferrer" 
              aria-label="LinkedIn" 
              tabIndex={0}
            >
              <LinkedInIcon />
            </SocialIcon>
            <SocialIcon 
              href="https://www.instagram.com/ecofabequipamentos?igsh=NnljaGppNWR6Zndj&utm_source=qr" 
              target="_blank" 
              rel="noopener noreferrer" 
              aria-label="Instagram" 
              tabIndex={0}
            >
              <InstagramIcon />
            </SocialIcon>
            <SocialIcon 
              href="https://www.youtube.com/@ecofabequipamentos5730" 
              target="_blank" 
              rel="noopener noreferrer" 
              aria-label="YouTube" 
              tabIndex={0}
            >
              <YouTubeIcon />
            </SocialIcon>
          </SocialIcons>
        </Column>
      </ContentWrapper>
      <CreditsBar>{translations.footer.credits}</CreditsBar>
    </FooterContainer>
  );
};

export default Footer;
